import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { TrafficLight } from '../components/TrafficLight';
import { DataLoader } from '../components/ForecastLoader';
import { getExecutives, getExecutive, getExecutiveClients, getExecutivePipeline } from '../lib/api';
import { formatNumber, formatPercent, formatMonth } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  ArrowLeft,
  Users,
  TrendingUp,
  Briefcase,
  MapPin,
  Mail,
  ArrowRight
} from 'lucide-react';

// Executive Detail Page
const ExecutiveDetailPage = ({ executiveId }) => {
  const navigate = useNavigate();
  const [executive, setExecutive] = useState(null);
  const [clients, setClients] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [execRes, clientsRes, pipelineRes] = await Promise.all([
          getExecutive(executiveId),
          getExecutiveClients(executiveId),
          getExecutivePipeline(executiveId)
        ]);
        setExecutive(execRes.data);
        setClients(clientsRes.data);
        setPipeline(pipelineRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [executiveId]);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Detalle del Ejecutivo</h1>
            <p className="text-slate-500 mt-1">Cargando información...</p>
          </div>
          <DataLoader type="executives" />
        </div>
      </Layout>
    );
  }

  if (!executive) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Ejecutivo no encontrado</p>
        </div>
      </Layout>
    );
  }

  // Sort clients by potential
  const sortedClients = [...clients].sort((a, b) => {
    const aUntapped = a.penta_total - (a.history?.[a.history.length - 1]?.kgs || 0);
    const bUntapped = b.penta_total - (b.history?.[b.history.length - 1]?.kgs || 0);
    return bUntapped - aUntapped;
  });

  // Stats
  const stats = {
    total: clients.length,
    green: clients.filter(c => c.status === 'green').length,
    yellow: clients.filter(c => c.status === 'yellow').length,
    red: clients.filter(c => c.status === 'red').length,
    totalVolume: clients.reduce((acc, c) => acc + (c.history?.[c.history.length - 1]?.kgs || 0), 0),
    openPipeline: pipeline.filter(p => p.status === 'open').length,
    pipelineKgs: pipeline.filter(p => p.status === 'open').reduce((acc, p) => acc + p.potential_kgs, 0)
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="executive-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/ejecutivos')}
            data-testid="back-to-executives-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Avatar className="w-16 h-16">
            <AvatarImage src={executive.avatar_url} alt={executive.name} />
            <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xl font-bold">
              {executive.initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{executive.name}</h1>
            <div className="flex items-center gap-4 mt-1 text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {executive.region}
              </span>
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {executive.email}
              </span>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500 mb-1">Clientes</p>
            <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1">
                <TrafficLight status="green" size="sm" />
                <span className="text-xs text-slate-600">{stats.green}</span>
              </div>
              <div className="flex items-center gap-1">
                <TrafficLight status="yellow" size="sm" />
                <span className="text-xs text-slate-600">{stats.yellow}</span>
              </div>
              <div className="flex items-center gap-1">
                <TrafficLight status="red" size="sm" />
                <span className="text-xs text-slate-600">{stats.red}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500 mb-1">Volumen Total</p>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(stats.totalVolume)}</p>
            <p className="text-xs text-slate-400 mt-2">kgs mensuales</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500 mb-1">Pipeline Activo</p>
            <p className="text-3xl font-bold text-slate-900">{stats.openPipeline}</p>
            <p className="text-xs text-slate-400 mt-2">oportunidades</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500 mb-1">Potencial Pipeline</p>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(stats.pipelineKgs)}</p>
            <p className="text-xs text-slate-400 mt-2">kgs proyectados</p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Clients */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-400" />
                Clientes Asignados
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">Ordenados por potencial de captura</p>
            </div>
            <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
              {sortedClients.map((client, i) => {
                const lastVolume = client.history?.[client.history.length - 1]?.kgs || 0;
                const untapped = client.penta_total - lastVolume;
                return (
                  <div 
                    key={client.id} 
                    className="p-4 hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-4"
                    onClick={() => navigate(`/clientes/${client.id}`)}
                  >
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-sm font-bold text-slate-500">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <TrafficLight status={client.status} size="sm" />
                        <p className="font-medium text-slate-900 truncate">{client.name}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{client.industry} • {client.route}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">{formatNumber(lastVolume)} kgs</p>
                      <p className="text-xs text-slate-500">{formatPercent(client.share_of_wallet, 0)} share</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-600">+{formatNumber(untapped)}</p>
                      <p className="text-xs text-slate-500">potencial</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pipeline */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-slate-400" />
                Pipeline
              </h2>
            </div>
            <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
              {pipeline.filter(p => p.status === 'open').length > 0 ? (
                pipeline.filter(p => p.status === 'open').map((opp) => (
                  <div key={opp.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-sm font-medium text-slate-900">{opp.client_name}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        opp.probability >= 70 ? 'bg-emerald-100 text-emerald-700' :
                        opp.probability >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {opp.probability}%
                      </span>
                    </div>
                    <p className="text-lg font-bold text-slate-900">{formatNumber(opp.potential_kgs)} kgs</p>
                    <p className="text-xs text-slate-500 mt-1">{opp.description}</p>
                    <p className="text-xs text-slate-400 mt-2">Cierre estimado: {formatMonth(opp.estimated_close_month)}</p>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-slate-500">
                  Sin oportunidades en pipeline
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Main Executives List Page
const EjecutivosPage = () => {
  const navigate = useNavigate();
  const { executiveId } = useParams();
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getExecutives();
        setExecutives(response.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (executiveId) {
    return <ExecutiveDetailPage executiveId={executiveId} />;
  }

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Ejecutivos</h1>
            <p className="text-slate-500 mt-1">Cargando equipo de ventas...</p>
          </div>
          <DataLoader type="executives" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="ejecutivos-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Ejecutivos</h1>
          <p className="text-slate-500 mt-1">{executives.length} ejecutivos de ventas</p>
        </div>

        {/* Executive Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {executives.map((exec) => (
            <div
              key={exec.id}
              onClick={() => navigate(`/ejecutivos/${exec.id}`)}
              className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
              data-testid={`executive-card-${exec.id}`}
            >
              <div className="flex items-center gap-4 mb-4">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={exec.avatar_url} alt={exec.name} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-lg font-bold">
                    {exec.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                    {exec.name}
                  </h3>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {exec.region}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Volumen</p>
                  <p className="text-lg font-bold text-slate-900">{formatNumber(exec.total_volume)}</p>
                  <p className="text-xs text-slate-400">kgs/mes</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Crecimiento</p>
                  <p className={`text-lg font-bold ${exec.growth_rate >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {exec.growth_rate >= 0 ? '+' : ''}{exec.growth_rate.toFixed(1)}%
                  </p>
                  <p className="text-xs text-slate-400">vs período ant.</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Briefcase className="w-3.5 h-3.5" />
                  <span>{exec.open_opportunities} oportunidades</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default EjecutivosPage;
